import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import {
  InvalidPasswordException,
  LimitExceededException,
  NotAuthorizedException,
} from 'src/app/shared/errors/cognito/cognito-errors';
import { passwordValidator } from 'src/app/shared/validators/registration-validators';
import { sameValueTwoFields } from 'src/app/utils/form-validator';
import { RoutesKeys } from 'src/app/utils/routes';

@Component({
  selector: 'app-change-known-password',
  templateUrl: './change-known-password.component.html',
  styleUrls: ['./change-known-password.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Change known password
 */
export class ChangeKnownPasswordComponent implements OnInit {
  RoutesKeys = RoutesKeys;
  formGroup = new FormGroup({
    currentPassword: new FormControl('', [Validators.required]),
    newPassword: new FormControl('', [
      Validators.required,
      passwordValidator(),
    ]),
    confirmNewPassword: new FormControl('', [Validators.required]),
  });
  text = {
    header: 'change-your-password',
    info: 'change-your-password-informative-text',
    currentPassword: 'current-password',
    newPassword: 'new-password',
    confirmNewPassword: 'confirm-new-password',
    changePassword: 'change-password',
    cancelChange: 'cancel-change',
    passwordChanged: 'password-successfully-changed',
    forgotPassword: 'forgot-your-password',
    invalidPasswordError: 'error-invalid-password',
    wrongPasswordError: 'error-wrong-password',
    unknownError: 'error-change-password-unknown',
    limitExceededError: 'error-change-password-limit-exceeded',
    invalidChangePasswordForm: 'invalid-change-password-form',
  };

  /**
   * @constructor
   */
  constructor(
    private readonly snackBarService: SnackBarService,
    private readonly translationService: TranslationService,
    private readonly userService: UserService,
    private readonly router: Router
  ) {}

  /**
   * on init
   */
  ngOnInit(): void {
    sameValueTwoFields(
      this.newPasswordControl,
      this.confirmNewPasswordControl,
      [Validators.required]
    );
    this.translationService.translateObject(this.text);
  }

  /**
   * Get current password control
   */
  get currentPasswordControl() {
    return this.formGroup.controls['currentPassword'];
  }

  /**
   * Get new password control
   */
  get newPasswordControl() {
    return this.formGroup.controls['newPassword'];
  }

  /**
   * Get confirm new password control
   */
  get confirmNewPasswordControl() {
    return this.formGroup.controls['confirmNewPassword'];
  }

  /**
   * Cancels changing password
   */
  cancelChange() {
    this.router.navigate([RoutesKeys.ACCOUNT_SETTINGS]);
  }

  /**
   * Actually change password
   */
  changePassword() {
    if (this.formGroup.invalid) {
      throw new Error(this.text.invalidChangePasswordForm);
    }

    // I can safely bang this out because `formGroup` must be valid for this
    // line to execute
    const currentPassword = this.currentPasswordControl.value!;
    const newPassword = this.newPasswordControl.value!;

    this.userService.changePassword(currentPassword, newPassword).subscribe({
      next: () => {
        this.router.navigate([RoutesKeys.ACCOUNT_SETTINGS]);
        this.snackBarService.success(this.text.passwordChanged);
      },
      error: (err: Error) => {
        this.handleError(err);
      },
    });
  }

  /**
   * Handles errors
   * @param {Error} err
   */
  private handleError(err: Error) {
    if (err instanceof InvalidPasswordException) {
      // Should never get here unless we change password validation rules
      this.snackBarService.error(this.text.invalidPasswordError);
    } else if (err instanceof NotAuthorizedException) {
      this.snackBarService.error(this.text.wrongPasswordError);
    } else if (err instanceof LimitExceededException) {
      this.snackBarService.error(this.text.limitExceededError);
    } else {
      this.snackBarService.error(this.text.unknownError);
    }
  }
}
